import { QueryKey, useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  create,
  fetch,
  findDistinctTypes,
  get,
  SpecialPeriod,
  SpecialPeriodCreationPayload,
  SpecialPeriodListParams,
  SpecialPeriodUpdatePayload,
  update,
} from '../api/special-periods'

/**
 * Query hook for fetching a single special period
 */
export const useSpecialPeriodQuery = (
  id?: string,
  options?: QueryOptions,
): UseQueryResult<SpecialPeriod, any> =>
  useResource<SpecialPeriod>('special-periods', get, id, undefined, options)

/**
 * Query hook for fetching multiple special periods with optional filters
 */
export const useSpecialPeriodsQuery = (
  filters?: SpecialPeriodListParams,
  options?: QueryOptions,
): UseQueryResult<SpecialPeriod[], any> =>
  useResources<SpecialPeriod[], SpecialPeriodListParams>(
    'special-periods',
    fetch,
    filters || {},
    options,
  )

/**
 * Mutation success handler - invalidates special periods queries
 */
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['special-periods'])
}

/**
 * Create mutation key
 */
export const useCreateMutationKey = (): QueryKey => ['special-periods', 'create']

/**
 * Mutation hook for creating a special period
 */
export const useCreateMutation = (): UseMutationResult<
  SpecialPeriod,
  SpecialPeriodCreationPayload,
  any
> => useMutation(create, { mutationKey: useCreateMutationKey(), onSuccess: onMutationSuccess })

/**
 * Update mutation key
 */
export const useUpdateMutationKey = (): QueryKey => ['special-periods', 'update']

/**
 * Mutation hook for updating a special period
 */
export const useUpdateMutation = (): UseMutationResult<
  SpecialPeriod,
  SpecialPeriodUpdatePayload,
  any
> =>
  useMutation(update, {
    mutationKey: useUpdateMutationKey(),
    onSuccess: onMutationSuccess,
  })

/**
 * Delete mutation key
 */
export const useDeleteMutationKey = (): QueryKey => ['special-periods', 'delete']

/**
 * Query hook for fetching distinct types of special periods for a given employer
 */
export const useSpecialPeriodTypesQuery = (
  employerId?: string,
  options?: QueryOptions<string[]>,
): UseQueryResult<string[], any> =>
  useResource<string[]>('special-periods-types', findDistinctTypes, employerId, undefined, options)
